import Bowser from "bowser";
import {getUrlQueryString, safeBase64Decode} from "./utils";
import {NativeDevice} from "./native-bridge";

const ua = window.navigator.userAgent;
const parser = Bowser.getParser(ua);
const result = parser.getResult();


function checkWebGL() {
    if (window.WebGLRenderingContext) {
        const canvas = document.createElement('canvas');
        const names = ['webgl2', 'webgl', 'experimental-webgl', 'moz-webgl', 'webkit-3d'];
        for (let i = 0; i < names.length; i += 1) {
            try {
                // eslint-disable-next-line
                const context = canvas.getContext(names[i]) as any;
                if (context && typeof context.getParameter === 'function') {
                    return true;
                }
            } catch (e) {
                //
            }
        }
        return false;
    }
    return false;
}

function checkWebAssembly() {
    try {
        if (typeof window.WebAssembly === 'object' && typeof window.WebAssembly.instantiate === 'function') {
            const module = new window.WebAssembly.Module(Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00));
            if (module instanceof window.WebAssembly.Module) {
                return new window.WebAssembly.Instance(module) instanceof window.WebAssembly.Instance;
            }
        }
        return false;
    } catch (e) {
        return false;
    }
}

function getX5Version(ua: string) {
    let version = "0";
    const key = "TBS/";
    let start = ua.indexOf(key);
    if (start !== -1) {
        start = start + key.length;
        version = ua.substr(start, ua.indexOf(" ", start) - start);
    }

    return parseInt(version);
}

function getNativeDevice(): NativeDevice | null {
    let nativeDevice = getUrlQueryString("device");
    if (nativeDevice === null || nativeDevice.length === 0) {
        return null;
    }

    nativeDevice = safeBase64Decode(nativeDevice);
    nativeDevice = atob(nativeDevice);
    const d = JSON.parse(nativeDevice) as NativeDevice;

    return d;
}

function getIsInApp(): boolean {
    const inApp = getUrlQueryString("inapp");
    return !!(inApp && inApp === "1");
}

function getIsOffline(): boolean {
    const offline = getUrlQueryString("offline");
    return !!(offline && offline === "1");
}

function getIsChatHelper(): boolean {
    const helper = getUrlQueryString("chatHelper");
    return !!(helper && helper === '1');
}

function getRecordLayout(): string | null {
    return getUrlQueryString("recordLayout");
}

const x5Version = getX5Version(ua);
const isInApp = getIsInApp();
const isOffline = getIsOffline();
const isChatHelper = getIsChatHelper();
const recordLayout = getRecordLayout();
const nativeDevice = getNativeDevice();

const $d = {
    /**
     * 是否为 Windows 操作系统
     */
    isWindows: result.os.name === "Windows",

    /**
     * 是否为 macOS 操作系统
     */
    isMacOs: result.os.name === "macOS",

    /**
     * 是否为移动端设备，包含手机和平板
     */
    isMobile: result.platform.type === "mobile" || result.platform.type === "tablet",

    /**
     * 是否为手机设备，不含平板
     */
    isMobileOnly: result.platform.type === "mobile",

    /**
     * 是否为平板设备
     */
    isTablet: result.platform.type === "tablet",

    /**
     * 是否为PC桌面设备
     */
    isDesktop: result.platform.type === "desktop",

    /**
     * 是否为安卓系统
     */
    isAndroid: result.os.name === "Android",

    /**
     * 是否为 iOS 系统
     */
    isIOS: result.os.name === "iOS",

    /**
     * 是否为 iPad 设备
     */
    isIPad: false,

    /**
     * 是否为谷歌浏览器
     */
    isChrome: result.browser.name === "Chrome",

    /**
     * 是否为火狐浏览器
     */
    isFirefox: result.browser.name === "Firefox",

    /**
     * 是否为 Safari 浏览器
     */
    isSafari:  result.browser.name === "Safari",

    /**
     * 是否为 IE 浏览器
     */
    isIE: result.browser.name === "Internet Explorer",

    /**
     * 是否为微软 Edge 浏览器
     */
    isEdge: result.browser.name === "Microsoft Edge",

    /**
     * 是否为微信内置浏览器
     */
    isWeChat: result.browser.name === "WeChat",

    /**
     * 是否为钉钉内置浏览器
     */
    isDingTalk: false,

    /**
     * 是否为腾讯X5内核
     */
    isX5: x5Version > 0,

    /**
     * 是否在 App 环境内
     */
    isInApp: isInApp,

    /**
     * 是否离线播放
     */
    isOffline: isOffline,

    /**
     * 是否互动助手模式
     */
    isChatHelper: isChatHelper,

    /**
     * 本地录制界面布局
     */
    recordLayout: recordLayout,

    deviceType: result.platform.type,

    deviceName: nativeDevice ? nativeDevice.device : "",

    osName: result.os.name,

    osVersion: result.os.version,

    browserName: result.browser.name,

    browserVersion: result.browser.version ? parseInt(result.browser.version) : 0,

    browserFullVersion: result.browser.version,

    engineName: result.engine.name,

    engineVersion: result.engine.version,

    x5Version: x5Version,

    appName: nativeDevice ? nativeDevice.appName : "",

    appVersion: nativeDevice ? nativeDevice.appVersion : "",

    sdkVersion: nativeDevice && nativeDevice.sdkVersion ? nativeDevice.sdkVersion : 0,

    resolution: nativeDevice ? nativeDevice.resolution : "",

    network: nativeDevice ? nativeDevice.network : "",

    ua: ua,

    webgl: checkWebGL(),

    wasm: checkWebAssembly(),

    width: window.screen.width,

    height: window.screen.height,
}

const chromeEngine = ua.match(/Chrome\/(\d+)/);
if (chromeEngine) {
    $d.engineName = "Chrome";
    $d.engineVersion = chromeEngine[1];
}

const isDingTalk = ua.match(/DingTalk/);
if (isDingTalk) {
    $d.isDingTalk = true;
}

// fix for iPad 13 +
// https://stackoverflow.com/questions/58019463/how-to-detect-device-name-in-safari-on-ios-13-while-it-doesnt-show-the-correct
// eslint-disable-next-line
// @ts-ignore
if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 && !window.MSStream) {
    $d.deviceType = "tablet";
    $d.osName = "iOS";
    $d.osVersion = $d.browserFullVersion;
    $d.isIOS = true;
    $d.isMobile = true;
    $d.isTablet = true;
    $d.isMacOs = false;
    $d.isDesktop = false;
}

if ($d.isIOS && $d.isTablet) {
    $d.isIPad = true;
}

export default $d;
